import React from "react";
import { MailIcon } from "../../components/Icon/MailIcon";
import type { MailIconProps } from "../../components/Icon/MailIcon.types";
// 直接从实现文件引入，不走本模块 barrel（./index 会再 re-export 本文件，
// 形成 MailIconWithBadge ↔ notification/index 自反环）
import utils from "../../utils/utils";
import { useMessageNotification } from "./useMessageNotification";
import { useBatchBonusMailBadge } from "./useBatchBonusMailBadge";

/**
 * 消息未读数联动的 MailIcon 包装组件。
 *
 * 为什么存在：
 * - `components/Icon/MailIcon` 必须保持 dumb（不引 modules/notification），
 *   否则会形成 `Icon → notification → user → components → Icon` 巨型循环依赖，
 *   直接破坏 tree-shaking 与 lazy 路由拆包。
 * - 业务侧若需要"自动联动未读数"的 MailIcon，使用此包装组件即可。
 *
 * 调用方不要从 `@/components/...` 里引，一律从 `@/modules/notification` 引，
 * 保证依赖方向永远是：业务代码 → modules/notification → components/Icon。
 */
export const MailIconWithBadge: React.FC<Omit<MailIconProps, "badgeContent" | "showBadge">> = props => {
  const { messageUnreadCount } = useMessageNotification();
  const { count: batchBonusCount, icon: batchBonusIcon } = useBatchBonusMailBadge();

  // 有未读的"批量赠金"站内信时：图标切换为该通知图标、角标显示未读批量赠金条数；
  // 否则回退：默认邮件图标 + 站内信总未读数
  const hasBatchBonus = batchBonusCount > 0;
  const badgeContent = hasBatchBonus ? batchBonusCount : messageUnreadCount;
  const batchBonusIconUrl =
    hasBatchBonus && batchBonusIcon ? utils.getImageUrl(batchBonusIcon) : undefined;

  return (
    <MailIcon
      {...props}
      iconSrc={batchBonusIconUrl}
      badgeContent={badgeContent}
      showBadge={badgeContent > 0}
    />
  );
};

export default MailIconWithBadge;
